import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ClientDownloads from '../components/ClientDownloads';
import PtoTracker from '../components/PtoTracker';
import SceneScrubAdmin from '../components/SceneScrubAdmin';
import ScrubQueueAdmin from '../components/ScrubQueueAdmin';

// ── Constants ─────────────────────────────────────────────────────────────────

const SERVICE_TYPES = [
  'In_House_Live', 'Off_Site_Live', 'Off_Site_Skit',
  'Off_Site_Pod', 'Off_Site_Riff', 'In_House_Riff', 'CTF', 'Cooking',
];

const SCRIPT_FORMATS = ['Skit', 'TH', 'AI', 'Mockumentary', 'None', 'Recipe', 'Longform'];

const PRODUCTION_TRACKS = ['Live', 'AI', 'Both'];

const LOCATION_TYPES = ['SSD', 'Frame.io', 'Cloud', 'Other'];

const ROLES = ['Writer', 'Qual_Reviewer', 'Editor', 'Producer', 'Post_Lead', 'Admin', 'Manager', 'Power_User'];

const AUDIT_ENTITY_TYPES = [
  'script', 'live_production', 'ai_production',
  'client', 'team_member', 'posting_scheduler',
];

// ── Role badge helper ─────────────────────────────────────────────────────────

function roleBadgeClass(role) {
  const map = {
    Writer:        'adm-badge adm-badge-writer',
    Admin:         'adm-badge adm-badge-admin',
    Qual_Reviewer: 'adm-badge adm-badge-reviewer',
    Editor:        'adm-badge adm-badge-editor',
    Producer:    'adm-badge adm-badge-shoot',
    Post_Lead:     'adm-badge adm-badge-post',
    Manager:       'adm-badge adm-badge-manager',
    Power_User:    'adm-badge adm-badge-admin',
  };
  return map[role] || 'adm-badge adm-badge-writer';
}

function roleLabel(role) {
  return role.replace(/_/g, ' ');
}

// ── Format helpers ────────────────────────────────────────────────────────────

function fmtDateTime(str) {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ── Empty form defaults ───────────────────────────────────────────────────────

function emptyClient() {
  return {
    name: '', initials: '', serviceType: 'In_House_Live',
    productionTrack: 'Live', contactName: '', contactEmail: '',
    contactPhone: '', postingChannel: '', allowedScriptFormats: [], notes: '',
  };
}

const INTERNAL_ROLES = ['Writer', 'Qual_Reviewer', 'Editor', 'Producer', 'Post_Lead', 'Admin', 'Manager', 'Power_User'];
const EXTERNAL_ROLES = ['Writer', 'Qual_Reviewer', 'Editor', 'Manager'];

function emptyMember() {
  return { name: '', initials: '', email: '', password: '', roles: [], isExternal: false, assignedClientIds: [], talentGender: '' };
}

function emptyStorage() {
  return { label: '', locationType: 'SSD', pathOrUrl: '', notes: '' };
}

// ── Client Modal ──────────────────────────────────────────────────────────────

function ClientModal({ client, onClose, onSaved }) {
  const isEdit = Boolean(client?.id);
  const [form, setForm] = useState(
    isEdit
      ? { ...client, allowedScriptFormats: client.allowedScriptFormats || [] }
      : emptyClient()
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(field, val) {
    setForm(f => ({ ...f, [field]: val }));
  }

  function toggleFormat(fmt) {
    setForm(f => ({
      ...f,
      allowedScriptFormats: f.allowedScriptFormats.includes(fmt)
        ? f.allowedScriptFormats.filter(x => x !== fmt)
        : [...f.allowedScriptFormats, fmt],
    }));
  }

  async function handleSave() {
    if (!form.name.trim() || !form.initials.trim() || !form.serviceType || !form.productionTrack) {
      setErr('Name, Initials, Service Type, and Production Track are required.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      if (isEdit) {
        await api.patch(`/api/clients/${client.id}`, form);
      } else {
        await api.post('/api/clients', form);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" style={{ width: 560 }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div>
            <div className="vc-label">Admin / Projects</div>
            <div className="vc-title">{isEdit ? 'Edit Project' : 'New Project'}</div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        <div className="vc-body">
          <div className="vc-field">
            <label>Name *</label>
            <input className="vc-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Project name" />
          </div>
          <div className="vc-field">
            <label>Initials *</label>
            <input className="vc-input" value={form.initials} onChange={e => set('initials', e.target.value)} placeholder="e.g. JD" maxLength={6} />
          </div>
          <div className="vc-field">
            <label>Service Type *</label>
            <select className="vc-input" value={form.serviceType} onChange={e => set('serviceType', e.target.value)}>
              {SERVICE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div className="vc-field">
            <label>Production Track *</label>
            <select className="vc-input" value={form.productionTrack} onChange={e => set('productionTrack', e.target.value)}>
              {PRODUCTION_TRACKS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="vc-field">
            <label>Contact Name</label>
            <input className="vc-input" value={form.contactName || ''} onChange={e => set('contactName', e.target.value)} placeholder="Full name" />
          </div>
          <div className="vc-field">
            <label>Contact Email</label>
            <input className="vc-input" type="email" value={form.contactEmail || ''} onChange={e => set('contactEmail', e.target.value)} placeholder="email@example.com" />
          </div>
          <div className="vc-field">
            <label>Contact Phone</label>
            <input className="vc-input" value={form.contactPhone || ''} onChange={e => set('contactPhone', e.target.value)} placeholder="+1 555 000 0000" />
          </div>
          <div className="vc-field">
            <label>Posting Channel</label>
            <input className="vc-input" value={form.postingChannel || ''} onChange={e => set('postingChannel', e.target.value)} placeholder="@handle or URL" />
          </div>
          <div className="vc-field full">
            <label>Allowed Script Formats</label>
            <div className="adm-role-checks">
              {SCRIPT_FORMATS.map(fmt => (
                <label key={fmt} className="adm-role-check">
                  <input
                    type="checkbox"
                    checked={form.allowedScriptFormats.includes(fmt)}
                    onChange={() => toggleFormat(fmt)}
                  />
                  {fmt === 'TH' ? 'Talking Head' : fmt}
                </label>
              ))}
            </div>
          </div>
          <div className="vc-field full">
            <label>Notes</label>
            <textarea className="vc-input" value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Any additional notes…" rows={3} />
          </div>
          {err && (
            <div className="vc-field full" style={{ color: 'var(--s-incomplete-t)', fontSize: 12 }}>{err}</div>
          )}
        </div>

        <div className="vc-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Team Member Modal ─────────────────────────────────────────────────────────

function MemberModal({ member, clients, talentScoped = false, onClose, onSaved }) {
  const isEdit = Boolean(member?.id);
  const [form, setForm] = useState(
    isEdit
      ? {
          name:             member.name,
          initials:         member.initials,
          isActive:         member.isActive,
          roles:            member.roles?.map(r => r.role) || [],
          isExternal:        member.isExternal       || false,
          assignedClientIds: member.assignedClients?.map(c => c.id) || [],
          talentGender:      member.talentGender || '',
        }
      : emptyMember()
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const firstName = form.name.trim().split(' ')[0].toLowerCase();
  const passwordPlaceholder = firstName ? `${firstName}_temp123` : 'firstname_temp123';
  const availableRoles = form.isExternal ? EXTERNAL_ROLES : INTERNAL_ROLES;

  function set(field, val) {
    setForm(f => ({ ...f, [field]: val }));
  }

  function toggleRole(role) {
    setForm(f => ({
      ...f,
      roles: f.roles.includes(role)
        ? f.roles.filter(r => r !== role)
        : [...f.roles, role],
    }));
  }

  function handleExternalToggle(isExternal) {
    setForm(f => ({
      ...f,
      isExternal,
      assignedClientIds: isExternal ? f.assignedClientIds : [],
      // Strip restricted roles when switching to external
      roles: isExternal
        ? f.roles.filter(r => EXTERNAL_ROLES.includes(r))
        : f.roles,
    }));
  }

  async function handleSave() {
    if (!form.name.trim() || !form.initials.trim() || (!isEdit && !form.email.trim())) {
      setErr('Name, Initials, and Email are required.');
      return;
    }
    if (form.isExternal && form.assignedClientIds.length === 0) {
      setErr('External members must be assigned to at least one project.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      if (isEdit) {
        await api.patch(`/api/team-members/${member.id}`, {
          name:              form.name,
          initials:          form.initials,
          isActive:          form.isActive,
          isExternal:        form.isExternal,
          assignedClientIds: form.isExternal ? form.assignedClientIds : [],
          talentGender:      form.talentGender || null,
        });
        // Role changes are admin-only; talent-scoped editors skip the roles PUT.
        if (!talentScoped) {
          await api.put(`/api/team-members/${member.id}/roles`, { roles: form.roles });
        }
      } else {
        await api.post('/api/team-members', {
          name:              form.name,
          initials:          form.initials,
          email:             form.email,
          password:          form.password.trim() || passwordPlaceholder,
          roles:             talentScoped ? ['Talent'] : form.roles,
          isExternal:        talentScoped ? false : form.isExternal,
          assignedClientIds: (!talentScoped && form.isExternal) ? form.assignedClientIds : [],
          talentGender:      form.talentGender || null,
        });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div>
            <div className="vc-label">Admin / Team</div>
            <div className="vc-title">{isEdit ? 'Edit Member' : 'New Team Member'}</div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        <div className="vc-body">
          <div className="vc-field">
            <label>Name *</label>
            <input className="vc-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" />
          </div>
          <div className="vc-field">
            <label>Initials *</label>
            <input className="vc-input" value={form.initials} onChange={e => set('initials', e.target.value)} placeholder="e.g. JD" maxLength={4} />
          </div>

          {!isEdit && (
            <>
              <div className="vc-field full">
                <label>Email *</label>
                <input className="vc-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="member@example.com" />
              </div>
              <div className="vc-field full">
                <label>Password</label>
                <input
                  className="vc-input"
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder={passwordPlaceholder}
                  autoComplete="new-password"
                />
                <span style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 2 }}>
                  Leave blank to use the default pattern shown above
                </span>
              </div>
            </>
          )}

          {isEdit && (
            <div className="vc-field">
              <label>Status</label>
              <select
                className="vc-input"
                value={form.isActive ? 'active' : 'inactive'}
                onChange={e => set('isActive', e.target.value === 'active')}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          {/* Member type toggle — admin only; talent are always internal */}
          {!talentScoped && (
          <div className="vc-field full">
            <label>Member Type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => handleExternalToggle(false)}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 'var(--radius-sm)', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)',
                  background: !form.isExternal ? 'var(--violet)' : 'var(--bg-card2)',
                  color: !form.isExternal ? '#fff' : 'var(--tx-muted)',
                  border: !form.isExternal ? 'none' : '1px solid var(--border)',
                }}
              >
                Internal
              </button>
              <button
                type="button"
                onClick={() => handleExternalToggle(true)}
                style={{
                  flex: 1, padding: '9px 0', borderRadius: 'var(--radius-sm)', fontSize: 13,
                  fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)',
                  background: form.isExternal ? 'var(--violet)' : 'var(--bg-card2)',
                  color: form.isExternal ? '#fff' : 'var(--tx-muted)',
                  border: form.isExternal ? 'none' : '1px solid var(--border)',
                }}
              >
                External
              </button>
            </div>
            {!form.isExternal && (
              <span style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 4 }}>
                Internal members can see all clients.
              </span>
            )}
          </div>
          )}

          {/* Client assignment — only for external, supports multiple */}
          {form.isExternal && (
            <div className="vc-field full">
              <label>Assigned Projects *</label>
              <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {clients.length === 0 && (
                  <span style={{ fontSize: 12, color: 'var(--tx-faint)' }}>No active projects</span>
                )}
                {clients.map(c => (
                  <label key={c.id} className="adm-role-check">
                    <input
                      type="checkbox"
                      checked={form.assignedClientIds.includes(c.id)}
                      onChange={() => setForm(f => ({
                        ...f,
                        assignedClientIds: f.assignedClientIds.includes(c.id)
                          ? f.assignedClientIds.filter(id => id !== c.id)
                          : [...f.assignedClientIds, c.id],
                      }))}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
              <span style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 4 }}>
                External members only see scripts and productions for their assigned clients.
              </span>
            </div>
          )}

          {!talentScoped && (
          <div className="vc-field full">
            <label>Roles</label>
            <div className="adm-role-checks">
              {availableRoles.map(role => (
                <label key={role} className="adm-role-check">
                  <input
                    type="checkbox"
                    checked={form.roles.includes(role)}
                    onChange={() => toggleRole(role)}
                  />
                  {roleLabel(role)}
                </label>
              ))}
            </div>
            {form.isExternal && (
              <span style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 4 }}>
                Admin, Power User, Producer, and Post Lead are not available for external members.
              </span>
            )}
          </div>
          )}

          {/* Talent. Admin sees a checkbox (grants the Talent role) + gender.
              In talent-scoped mode the member is always talent, so only the
              gender selector is shown. */}
          {talentScoped ? (
            <div className="vc-field full">
              <label>Gender (for Script List talent matching)</label>
              <select
                className="vc-input"
                value={form.talentGender}
                onChange={e => set('talentGender', e.target.value)}
              >
                <option value="">— Unset —</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="either">Either (can play any gender role)</option>
              </select>
            </div>
          ) : (
            <div className="vc-field full">
              <label>Talent</label>
              <label className="adm-role-check">
                <input
                  type="checkbox"
                  checked={form.roles.includes('Talent')}
                  onChange={() => toggleRole('Talent')}
                />
                This member is on-camera talent
              </label>
              {form.roles.includes('Talent') && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--tx-muted)', marginBottom: 4 }}>
                    Gender (for Script List talent matching)
                  </label>
                  <select
                    className="vc-input"
                    value={form.talentGender}
                    onChange={e => set('talentGender', e.target.value)}
                  >
                    <option value="">— Unset —</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="either">Either (can play any gender role)</option>
                  </select>
                </div>
              )}
            </div>
          )}

          {err && (
            <div className="vc-field full" style={{ color: 'var(--s-incomplete-t)', fontSize: 12 }}>{err}</div>
          )}
        </div>

        <div className="vc-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Member'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Storage Modal ─────────────────────────────────────────────────────────────

function StorageModal({ storage, onClose, onSaved }) {
  const isEdit = Boolean(storage?.id);
  const [form, setForm] = useState(isEdit ? { ...storage } : emptyStorage());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(field, val) {
    setForm(f => ({ ...f, [field]: val }));
  }

  async function handleSave() {
    if (!form.label.trim() || !form.locationType) {
      setErr('Label and Location Type are required.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      if (isEdit) {
        await api.patch(`/api/storage/${storage.id}`, form);
      } else {
        await api.post('/api/storage', form);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" style={{ width: 480 }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div>
            <div className="vc-label">Admin / Storage</div>
            <div className="vc-title">{isEdit ? 'Edit Storage Location' : 'New Storage Location'}</div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        <div className="vc-body">
          <div className="vc-field full">
            <label>Label *</label>
            <input className="vc-input" value={form.label} onChange={e => set('label', e.target.value)} placeholder="e.g. Main SSD, Frame.io Project…" />
          </div>
          <div className="vc-field">
            <label>Location Type *</label>
            <select className="vc-input" value={form.locationType} onChange={e => set('locationType', e.target.value)}>
              {LOCATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="vc-field full">
            <label>Path / URL</label>
            <input className="vc-input" value={form.pathOrUrl || ''} onChange={e => set('pathOrUrl', e.target.value)} placeholder="/Volumes/SSD/Projects or https://…" />
          </div>
          <div className="vc-field full">
            <label>Notes</label>
            <textarea className="vc-input" value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Any notes about this location…" rows={3} />
          </div>
          {err && (
            <div className="vc-field full" style={{ color: 'var(--s-incomplete-t)', fontSize: 12 }}>{err}</div>
          )}
        </div>

        <div className="vc-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Location'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Confirm Modal ─────────────────────────────────────────────────────────────

function ConfirmModal({ title, message, onConfirm, onClose, danger, confirmLabel }) {
  const [working, setWorking] = useState(false);

  async function go() {
    setWorking(true);
    try { await onConfirm(); } finally { setWorking(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div>
            <div className="vc-title">{title}</div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '22px 28px' }}>
          <p style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.6 }}>{message}</p>
        </div>
        <div className="vc-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            onClick={go}
            disabled={working}
            style={{
              background: danger ? 'rgba(212,85,85,0.15)' : 'var(--violet)',
              color: danger ? 'var(--s-incomplete-t)' : '#fff',
              border: danger ? '1px solid rgba(212,85,85,0.35)' : 'none',
              borderRadius: 'var(--radius-sm)',
              padding: '9px 18px',
              fontSize: 13,
              fontWeight: 600,
              cursor: working ? 'default' : 'pointer',
              opacity: working ? 0.6 : 1,
              fontFamily: 'var(--font)',
            }}
          >
            {working ? 'Working…' : (confirmLabel || 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Clients ──────────────────────────────────────────────────────────────

function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // null | { type: 'new'|'edit', client? }
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/clients?includeInactive=true');
      setClients(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleArchive(client) {
    await api.delete(`/api/clients/${client.id}`);
    load();
  }

  async function handleUnarchive(client) {
    await api.patch(`/api/clients/${client.id}`, { isActive: true });
    load();
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn-primary" onClick={() => setModal({ type: 'new' })}>+ New Project</button>
      </div>

      {loading && <div style={{ color: 'var(--tx-muted)', fontSize: 13, padding: '20px 0' }}>Loading…</div>}

      {!loading && (
        <div style={{ overflowX: 'auto' }}>
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th">Name</th>
                <th className="adm-th">Initials</th>
                <th className="adm-th">Service Type</th>
                <th className="adm-th">Track</th>
                <th className="adm-th">Contact</th>
                <th className="adm-th">Active</th>
                <th className="adm-th" style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {clients.length === 0 && (
                <tr>
                  <td className="adm-td" colSpan={7} style={{ textAlign: 'center', color: 'var(--tx-faint)', padding: '32px 16px' }}>
                    No clients yet
                  </td>
                </tr>
              )}
              {clients.map(c => (
                <tr key={c.id} className={!c.isActive ? 'adm-inactive' : ''}>
                  <td className="adm-td primary">{c.name}</td>
                  <td className="adm-td">
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--violet)' }}>{c.initials}</span>
                  </td>
                  <td className="adm-td">{c.serviceType?.replace(/_/g, ' ')}</td>
                  <td className="adm-td">{c.productionTrack}</td>
                  <td className="adm-td">
                    {c.contactName
                      ? <span title={c.contactEmail || ''}>{c.contactName}</span>
                      : <span style={{ color: 'var(--tx-faint)' }}>—</span>}
                  </td>
                  <td className="adm-td">
                    <span style={{
                      color: c.isActive ? 'var(--s-approved-t)' : 'var(--tx-faint)',
                      fontSize: 12, fontWeight: 600,
                    }}>
                      {c.isActive ? 'Active' : 'Archived'}
                    </span>
                  </td>
                  <td className="adm-td">
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="adm-action-btn"
                        title="Edit"
                        onClick={() => setModal({ type: 'edit', client: c })}
                      >
                        ✎
                      </button>
                      {c.isActive ? (
                        <button
                          className="adm-action-btn"
                          title="Archive"
                          style={{ color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.3)' }}
                          onClick={() => setConfirm({
                            title: 'Archive Project',
                            message: `Archive "${c.name}"? They will be hidden from active lists.`,
                            action: () => handleArchive(c),
                          })}
                        >
                          ⊘
                        </button>
                      ) : (
                        <button
                          className="adm-action-btn"
                          title="Restore"
                          style={{ color: 'var(--s-approved-t)', borderColor: 'rgba(77,170,112,0.3)' }}
                          onClick={() => handleUnarchive(c)}
                        >
                          ↺
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal?.type === 'new' && (
        <ClientModal
          client={null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {modal?.type === 'edit' && (
        <ClientModal
          client={modal.client}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          danger={confirm.danger !== false}
          confirmLabel={confirm.confirmLabel}
          onConfirm={async () => { await confirm.action(); setConfirm(null); }}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ── User Settings Modal (admin manages another user's notification prefs) ─────

const ROLE_NORM_MAP = {
  Qual_Reviewer: 'Qual Reviewer',
  Post_Lead: 'Post Lead',        Power_User: 'Power User',
};

function UserSettingsModal({ member, onClose }) {
  const [loading,      setLoading]      = useState(true);
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [eventTypes,   setEventTypes]   = useState([]);
  const [prefs,        setPrefs]        = useState({});
  const [saving,       setSaving]       = useState(false);
  const [msg,          setMsg]          = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/notifications/event-types'),
      api.get(`/api/notifications/admin/event-preferences/${member.id}`),
      api.get(`/api/notifications/admin/preferences/${member.id}`),
    ]).then(([typesRes, prefsRes, globalRes]) => {
      setEventTypes(typesRes.data);
      const map = {};
      prefsRes.data.forEach(p => { map[p.eventType] = p; });
      setPrefs(map);
      setNotifEnabled(globalRes.data.notificationsEnabled);
    }).catch(() => setMsg({ ok: false, text: 'Failed to load preferences.' }))
      .finally(() => setLoading(false));
  }, [member.id]);

  function togglePref(eventType, channel) {
    setPrefs(prev => ({
      ...prev,
      [eventType]: { ...prev[eventType], eventType, [channel]: !prev[eventType]?.[channel] },
    }));
    setMsg(null);
  }

  async function handleSave() {
    setSaving(true);
    setMsg(null);
    try {
      const payload = Object.values(prefs).map(p => ({
        eventType:    p.eventType,
        emailEnabled: p.emailEnabled ?? true,
        inAppEnabled: p.inAppEnabled ?? true,
      }));
      await Promise.all([
        api.patch(`/api/notifications/admin/preferences/${member.id}`, { notificationsEnabled: notifEnabled }),
        ...(payload.length > 0 ? [api.put(`/api/notifications/admin/event-preferences/${member.id}`, payload)] : []),
      ]);
      setMsg({ ok: true, text: 'Settings saved.' });
    } catch {
      setMsg({ ok: false, text: 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  }

  const memberRoles = (member.roles || []).map(r => ROLE_NORM_MAP[r.role] || r.role);

  function Toggle({ on, onToggle }) {
    return (
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: 36, height: 20, borderRadius: 10, flexShrink: 0,
          background: on ? 'var(--violet)' : 'var(--bg-input)',
          border: `1px solid ${on ? 'var(--violet)' : 'var(--border-mid)'}`,
          cursor: 'pointer', position: 'relative',
          transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        <span style={{
          position: 'absolute', top: 2, left: on ? 16 : 2,
          width: 14, height: 14, borderRadius: '50%', background: '#fff',
          transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }} />
      </button>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="vc-modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        <div className="vc-header">
          <div>
            <div className="vc-label">Admin / User Settings</div>
            <div className="vc-title">{member.name}</div>
          </div>
          <button className="vc-close" onClick={onClose}>✕</button>
        </div>

        <div className="vc-body">
          {loading ? (
            <div style={{ padding: '12px 0', fontSize: 13, color: 'var(--tx-faint)' }}>Loading…</div>
          ) : (
            <>
              {/* Global notifications toggle */}
              <div className="vc-field full">
                <label>All Notifications</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Toggle on={notifEnabled} onToggle={() => { setNotifEnabled(v => !v); setMsg(null); }} />
                  <span style={{ fontSize: 12, color: notifEnabled ? 'var(--tx-primary)' : 'var(--tx-faint)' }}>
                    {notifEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              {/* Per-event preferences */}
              <div className="vc-field full" style={{ marginTop: 4 }}>
                <label>Per-Event Preferences</label>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '0 0 8px', color: 'var(--tx-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Event</th>
                      <th style={{ textAlign: 'center', padding: '0 12px 8px', color: 'var(--tx-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>In-App</th>
                      <th style={{ textAlign: 'center', padding: '0 0 8px', color: 'var(--tx-muted)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eventTypes.map(t => {
                      const pref      = prefs[t.type] || {};
                      const inApp     = pref.inAppEnabled ?? true;
                      const email     = pref.emailEnabled ?? true;
                      const relevant  = t.roles.some(r => memberRoles.includes(r));
                      return (
                        <tr key={t.type} style={{ borderTop: '1px solid var(--border)', opacity: relevant ? 1 : 0.45 }}>
                          <td style={{ padding: '10px 0' }}>
                            <div style={{ color: 'var(--tx-primary)', fontWeight: 500, marginBottom: 1 }}>{t.label}</div>
                            <div style={{ color: 'var(--tx-faint)', fontSize: 11, lineHeight: 1.4 }}>{t.description}</div>
                            {!relevant && (
                              <div style={{ fontSize: 10, color: 'var(--tx-faint)', fontStyle: 'italic', marginTop: 2 }}>
                                Not applicable to this user's roles
                              </div>
                            )}
                          </td>
                          <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                            <Toggle on={inApp} onToggle={() => togglePref(t.type, 'inAppEnabled')} />
                          </td>
                          <td style={{ textAlign: 'center', padding: '10px 0' }}>
                            <Toggle on={email} onToggle={() => togglePref(t.type, 'emailEnabled')} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {msg && (
            <div style={{ fontSize: 12, color: msg.ok ? 'var(--s-approved-t)' : 'var(--s-incomplete-t)', padding: '4px 0' }}>
              {msg.text}
            </div>
          )}
        </div>

        <div className="vc-footer">
          <button className="btn-ghost" onClick={onClose}>Close</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Team Members ─────────────────────────────────────────────────────────

function TeamTab({ talentScoped = false }) {
  const [members,    setMembers]    = useState([]);
  const [clients,    setClients]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [modal,      setModal]      = useState(null);
  const [confirm,    setConfirm]    = useState(null);
  const [actionErr,  setActionErr]  = useState('');
  const [actionMsg,  setActionMsg]  = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, clientsRes] = await Promise.all([
        api.get('/api/team-members?includeInactive=true'),
        api.get('/api/clients'),
      ]);
      setMembers(membersRes.data);
      setClients(clientsRes.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDeactivate(member) {
    await api.patch(`/api/team-members/${member.id}`, { isActive: false });
    load();
  }

  async function handleActivate(member) {
    await api.patch(`/api/team-members/${member.id}`, { isActive: true });
    load();
  }

  async function handleHardDelete(member) {
    try {
      setActionErr('');
      await api.delete(`/api/team-members/${member.id}`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Delete failed.';
      setActionErr(msg);
    }
  }

  async function handleForcePasswordChange(member) {
    await api.patch(`/api/team-members/${member.id}`, { mustChangePassword: true });
    load();
  }

  async function handleSendReset(member) {
    try {
      setActionErr('');
      const res = await api.post(`/api/team-members/${member.id}/send-reset`);
      setActionMsg(res.data?.message || `Password reset email sent to ${member.name}.`);
      setTimeout(() => setActionMsg(''), 4000);
    } catch (e) {
      setActionErr(e?.response?.data?.error || 'Failed to send reset email.');
    }
  }

  // In talent-scoped mode (Power User / Producer) only talent are listed.
  const displayMembers = talentScoped
    ? members.filter(m => (m.roles || []).some(r => r.role === 'Talent'))
    : members;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn-primary" onClick={() => setModal({ type: 'new' })}>{talentScoped ? '+ New Talent' : '+ New Member'}</button>
      </div>

      {actionErr && (
        <div style={{ background: 'var(--s-incomplete)', color: 'var(--s-incomplete-t)', borderRadius: 8, padding: '10px 14px', fontSize: 12, marginBottom: 12 }}>
          {actionErr}
          <button onClick={() => setActionErr('')} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 12 }}>✕</button>
        </div>
      )}

      {actionMsg && (
        <div style={{ background: 'var(--s-approved)', color: 'var(--s-approved-t)', borderRadius: 8, padding: '10px 14px', fontSize: 12, marginBottom: 12 }}>
          {actionMsg}
        </div>
      )}

      {loading && <div style={{ color: 'var(--tx-muted)', fontSize: 13, padding: '20px 0' }}>Loading…</div>}

      {!loading && (
        <div style={{ overflowX: 'auto' }}>
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th">Name</th>
                <th className="adm-th">Initials</th>
                <th className="adm-th">Email</th>
                <th className="adm-th">Roles</th>
                <th className="adm-th">Active</th>
                <th className="adm-th" style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {displayMembers.length === 0 && (
                <tr>
                  <td className="adm-td" colSpan={6} style={{ textAlign: 'center', color: 'var(--tx-faint)', padding: '32px 16px' }}>
                    No team members yet
                  </td>
                </tr>
              )}
              {displayMembers.map(m => (
                <tr key={m.id} className={!m.isActive ? 'adm-inactive' : ''}>
                  <td className="adm-td primary">{m.name}</td>
                  <td className="adm-td">
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--violet)' }}>{m.initials}</span>
                  </td>
                  <td className="adm-td" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{m.email}</td>
                  <td className="adm-td">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                      {m.isExternal && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'rgba(168,130,245,0.15)', color: 'var(--violet)', border: '1px solid rgba(168,130,245,0.3)', whiteSpace: 'nowrap' }}>
                          External · {m.assignedClients?.map(c => c.name).join(', ') || '—'}
                        </span>
                      )}
                      {m.roles && m.roles.length > 0
                        ? m.roles.map(r => (
                            <span key={r.role} className={roleBadgeClass(r.role)}>
                              {roleLabel(r.role)}
                            </span>
                          ))
                        : <span style={{ color: 'var(--tx-faint)', fontSize: 12 }}>No roles</span>
                      }
                    </div>
                  </td>
                  <td className="adm-td">
                    <span style={{
                      color: m.isActive ? 'var(--s-approved-t)' : 'var(--tx-faint)',
                      fontSize: 12, fontWeight: 600,
                    }}>
                      {m.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="adm-td">
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {/* Edit */}
                      <button
                        className="adm-action-btn"
                        title="Edit member"
                        onClick={() => setModal({ type: 'edit', member: m })}
                      >
                        ✎
                      </button>
                      {/* User settings (notifications) */}
                      <button
                        className="adm-action-btn"
                        title="Manage user settings"
                        style={{ color: 'var(--tx-muted)', borderColor: 'var(--border-mid)' }}
                        onClick={() => setModal({ type: 'settings', member: m })}
                      >
                        ⚙
                      </button>
                      {/* Force password change */}
                      <button
                        className="adm-action-btn"
                        title="Force password change on next login"
                        style={{ color: 'var(--s-filming-t)', borderColor: 'rgba(168,130,245,0.35)' }}
                        onClick={() => setConfirm({
                          title: 'Force Password Change',
                          message: `"${m.name}" will be required to set a new password on their next login.`,
                          action: () => handleForcePasswordChange(m),
                        })}
                      >
                        ⚷
                      </button>
                      {/* Send password-reset email — confirmation-gated to avoid accidental sends */}
                      <button
                        className="adm-action-btn"
                        title="Send password-reset email"
                        style={{ color: 'var(--tx-muted)', borderColor: 'var(--border-mid)' }}
                        onClick={() => setConfirm({
                          title: 'Send Password Reset Email',
                          message: `Send a password-reset email to "${m.name}" (${m.email})? They'll get a link to set a new password.`,
                          danger: false,
                          confirmLabel: 'Send Email',
                          action: () => handleSendReset(m),
                        })}
                      >
                        ✉
                      </button>
                      {/* Deactivate / Reactivate */}
                      {m.isActive ? (
                        <button
                          className="adm-action-btn"
                          title="Deactivate (revoke login)"
                          style={{ color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.3)' }}
                          onClick={() => setConfirm({
                            title: 'Deactivate Member',
                            message: `Deactivate "${m.name}"? They will no longer be able to log in.`,
                            action: () => handleDeactivate(m),
                          })}
                        >
                          ⊘
                        </button>
                      ) : (
                        <button
                          className="adm-action-btn"
                          title="Reactivate"
                          style={{ color: 'var(--s-approved-t)', borderColor: 'rgba(77,170,112,0.3)' }}
                          onClick={() => handleActivate(m)}
                        >
                          ↺
                        </button>
                      )}
                      {/* Hard delete — admin only; hidden in talent-scoped mode */}
                      {!talentScoped && (
                        <button
                          className="adm-action-btn"
                          title="Delete permanently"
                          style={{ color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.3)' }}
                          onClick={() => setConfirm({
                            title: 'Delete Member',
                            message: `Delete "${m.name}"? They'll be removed from the team, lose all access immediately, and stop receiving any notifications or emails. Their historical records (scripts, production, audit logs) are kept. This can't be undone from here.`,
                            action: () => handleHardDelete(m),
                          })}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal?.type === 'new' && (
        <MemberModal
          member={null}
          clients={clients}
          talentScoped={talentScoped}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {modal?.type === 'edit' && (
        <MemberModal
          member={modal.member}
          clients={clients}
          talentScoped={talentScoped}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {modal?.type === 'settings' && (
        <UserSettingsModal
          member={modal.member}
          onClose={() => setModal(null)}
        />
      )}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          danger={confirm.danger !== false}
          confirmLabel={confirm.confirmLabel}
          onConfirm={async () => { await confirm.action(); setConfirm(null); }}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ── Tab: Storage Locations ────────────────────────────────────────────────────

function StorageTab() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/storage');
      setLocations(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(loc) {
    await api.delete(`/api/storage/${loc.id}`);
    load();
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn-primary" onClick={() => setModal({ type: 'new' })}>+ Add Location</button>
      </div>

      {loading && <div style={{ color: 'var(--tx-muted)', fontSize: 13, padding: '20px 0' }}>Loading…</div>}

      {!loading && (
        <div style={{ overflowX: 'auto' }}>
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th">Label</th>
                <th className="adm-th">Type</th>
                <th className="adm-th">Path / URL</th>
                <th className="adm-th">Notes</th>
                <th className="adm-th" style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {locations.length === 0 && (
                <tr>
                  <td className="adm-td" colSpan={5} style={{ textAlign: 'center', color: 'var(--tx-faint)', padding: '32px 16px' }}>
                    No storage locations yet
                  </td>
                </tr>
              )}
              {locations.map(loc => (
                <tr key={loc.id}>
                  <td className="adm-td primary">{loc.label}</td>
                  <td className="adm-td">
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--tx-secondary)' }}>
                      {loc.locationType}
                    </span>
                  </td>
                  <td className="adm-td" style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {loc.pathOrUrl
                      ? <span title={loc.pathOrUrl} style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{loc.pathOrUrl}</span>
                      : <span style={{ color: 'var(--tx-faint)' }}>—</span>}
                  </td>
                  <td className="adm-td" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {loc.notes
                      ? <span title={loc.notes}>{loc.notes}</span>
                      : <span style={{ color: 'var(--tx-faint)' }}>—</span>}
                  </td>
                  <td className="adm-td">
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        className="adm-action-btn"
                        title="Edit"
                        onClick={() => setModal({ type: 'edit', storage: loc })}
                      >
                        ✎
                      </button>
                      <button
                        className="adm-action-btn"
                        title="Delete"
                        style={{ color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.3)' }}
                        onClick={() => setConfirm({
                          title: 'Delete Location',
                          message: `Delete "${loc.label}"? This cannot be undone.`,
                          action: () => handleDelete(loc),
                        })}
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal?.type === 'new' && (
        <StorageModal
          storage={null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {modal?.type === 'edit' && (
        <StorageModal
          storage={modal.storage}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          danger={confirm.danger !== false}
          confirmLabel={confirm.confirmLabel}
          onConfirm={async () => { await confirm.action(); setConfirm(null); }}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ── Tab: Audit Log ────────────────────────────────────────────────────────────

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ entityType: '', from: '', to: '' });

  function setFilter(field, val) {
    setFilters(f => ({ ...f, [field]: val }));
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (filters.entityType) params.set('entityType', filters.entityType);
      if (filters.from)       params.set('from', filters.from);
      if (filters.to)         params.set('to', filters.to);
      const res = await api.get(`/api/audit?${params.toString()}`);
      setLogs(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [filters]);

  // Initial load on mount
  useEffect(() => {
    const params = new URLSearchParams({ limit: 100 });
    api.get(`/api/audit?${params.toString()}`)
      .then(res => setLogs(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    setLoading(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <select
          className="filter-input"
          value={filters.entityType}
          onChange={e => setFilter('entityType', e.target.value)}
          style={{ minWidth: 180 }}
        >
          <option value="">All Entity Types</option>
          {AUDIT_ENTITY_TYPES.map(t => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>

        <input
          className="filter-input"
          type="date"
          value={filters.from}
          onChange={e => setFilter('from', e.target.value)}
          style={{ colorScheme: 'dark' }}
          title="From date"
        />
        <span style={{ color: 'var(--tx-faint)', fontSize: 12 }}>to</span>
        <input
          className="filter-input"
          type="date"
          value={filters.to}
          onChange={e => setFilter('to', e.target.value)}
          style={{ colorScheme: 'dark' }}
          title="To date"
        />

        <button className="btn-primary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Load'}
        </button>
      </div>

      {loading && <div style={{ color: 'var(--tx-muted)', fontSize: 13, padding: '20px 0' }}>Loading…</div>}

      {!loading && (
        <div style={{ overflowX: 'auto' }}>
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th">Date / Time</th>
                <th className="adm-th">Entity Type</th>
                <th className="adm-th">Field</th>
                <th className="adm-th">Old Value</th>
                <th className="adm-th">New Value</th>
                <th className="adm-th">Changed By</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr>
                  <td className="adm-td" colSpan={6} style={{ textAlign: 'center', color: 'var(--tx-faint)', padding: '32px 16px' }}>
                    No audit entries found
                  </td>
                </tr>
              )}
              {logs.map(entry => (
                <tr key={entry.id}>
                  <td className="adm-td" style={{ fontFamily: 'var(--mono)', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {fmtDateTime(entry.changedAt)}
                  </td>
                  <td className="adm-td">
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--tx-secondary)' }}>
                      {entry.entityType?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="adm-td" style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--violet)' }}>
                    {entry.fieldChanged || '—'}
                  </td>
                  <td className="adm-td" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.oldValue != null
                      ? <span title={String(entry.oldValue)} style={{ color: 'var(--s-incomplete-t)' }}>{String(entry.oldValue)}</span>
                      : <span style={{ color: 'var(--tx-faint)' }}>—</span>}
                  </td>
                  <td className="adm-td" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.newValue != null
                      ? <span title={String(entry.newValue)} style={{ color: 'var(--s-approved-t)' }}>{String(entry.newValue)}</span>
                      : <span style={{ color: 'var(--tx-faint)' }}>—</span>}
                  </td>
                  <td className="adm-td">
                    {entry.changedByMember?.name
                      || <span style={{ color: 'var(--tx-faint)' }}>System</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Tab: Data Import / Export ─────────────────────────────────────────────────

// Parses CSV text (handles quoted fields) → array of row objects keyed by header
function parseCsvText(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  function parseRow(line) {
    const vals = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    vals.push(cur.trim());
    return vals;
  }

  const headers = parseRow(lines[0]);
  const rows    = lines.slice(1).map(line => {
    const vals = parseRow(line);
    const obj  = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

async function triggerDownload(path, filename) {
  const res = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([res.data]));
  const a   = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function ImportSection({ label, exportPath, templatePath, templateFile, importPath, exportFile, templates }) {
  const [file,     setFile]     = useState(null);
  const [preview,  setPreview]  = useState(null);  // { headers, rows }
  const [result,   setResult]   = useState(null);  // { created, errors }
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState('');

  function handleFileChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setResult(null); setErr('');
    const reader = new FileReader();
    reader.onload = ev => {
      const parsed = parseCsvText(ev.target.result);
      setPreview(parsed);
    };
    reader.readAsText(f);
  }

  async function handleImport() {
    if (!preview?.rows?.length) return;
    setLoading(true); setErr(''); setResult(null);
    try {
      const res = await api.post(importPath, { rows: preview.rows, headers: preview.headers });
      setResult(res.data);
      setFile(null); setPreview(null);
    } catch (e) {
      setErr(e?.response?.data?.error || 'Import failed.');
    } finally { setLoading(false); }
  }

  return (
    <div className="data-section">
      <div className="data-section-title">{label}</div>

      <div className="data-actions">
        <button className="btn-ghost" onClick={() => triggerDownload(exportPath, exportFile)}>↓ Export CSV</button>
        {Array.isArray(templates)
          ? templates.map(t => (
              <button key={t.label} className="btn-ghost" onClick={() => triggerDownload(t.path, t.file)}>
                ↓ Template: {t.label}
              </button>
            ))
          : <button className="btn-ghost" onClick={() => triggerDownload(templatePath, templateFile)}>↓ Download Template</button>
        }
      </div>

      <div className="data-import-row">
        <label className="data-file-label">
          {file ? file.name : 'Choose CSV file…'}
          <input type="file" accept=".csv,text/csv" onChange={handleFileChange} style={{ display: 'none' }} />
        </label>
        {preview && (
          <button className="btn-primary" onClick={handleImport} disabled={loading}>
            {loading ? 'Importing…' : `Import ${preview.rows.length} row${preview.rows.length !== 1 ? 's' : ''}`}
          </button>
        )}
      </div>

      {err && <div className="data-err">{err}</div>}

      {result && (
        <div className="data-result">
          <span style={{ color: 'var(--s-approved-t)' }}>✓ {result.created} record{result.created !== 1 ? 's' : ''} created</span>
          {result.errors?.length > 0 && (
            <div className="data-errors">
              {result.errors.map((e, i) => (
                <div key={i} className="data-error-row">Row {e.row}: {e.message}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {preview && preview.rows.length > 0 && (
        <div className="data-preview">
          <div className="data-preview-label">Preview — first {Math.min(5, preview.rows.length)} of {preview.rows.length} rows</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="adm-table" style={{ fontSize: 11 }}>
              <thead>
                <tr>{preview.headers.map(h => <th key={h} className="adm-th">{h}</th>)}</tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 5).map((row, ri) => (
                  <tr key={ri}>
                    {preview.headers.map(h => (
                      <td key={h} className="adm-td" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row[h]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function DataTab() {
  return (
    <div className="data-tab">
      <div style={{ color: 'var(--tx-muted)', fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
        Export existing data as CSV, or import records from a CSV file. Download a template to see the exact column format expected for each import.
        Client names must match exactly (case-insensitive). Writer/team member names must match as entered in the system.
      </div>

      <ImportSection
        label="Scripts"
        exportPath="/api/data/scripts/export"
        importPath="/api/data/scripts/import"
        exportFile="scripts-export.csv"
        templates={[
          { label: 'Skit',           path: '/api/data/scripts/template/skit',          file: 'scripts-skit-template.csv'          },
          { label: 'Talking Head',   path: '/api/data/scripts/template/th',            file: 'scripts-th-template.csv'            },
          { label: 'AI',             path: '/api/data/scripts/template/ai',            file: 'scripts-ai-template.csv'            },
          { label: 'Mockumentary',   path: '/api/data/scripts/template/mockumentary',  file: 'scripts-mockumentary-template.csv'  },
          { label: 'No Format',      path: '/api/data/scripts/template/none',          file: 'scripts-template.csv'               },
        ]}
      />
      <ImportSection
        label="Live Production"
        exportPath="/api/data/live-production/export"
        templatePath="/api/data/live-production/template"
        importPath="/api/data/live-production/import"
        exportFile="live-production-export.csv"
        templateFile="live-production-import-template.csv"
      />
      <ImportSection
        label="AI Production"
        exportPath="/api/data/ai-production/export"
        templatePath="/api/data/ai-production/template"
        importPath="/api/data/ai-production/import"
        exportFile="ai-production-export.csv"
        templateFile="ai-production-import-template.csv"
      />
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────────────────────────

const TABS = [
  { key: 'clients', label: 'Projects' },
  { key: 'team',    label: 'Team Members' },
  { key: 'storage', label: 'Storage' },
  { key: 'audit',   label: 'Audit Log' },
  { key: 'data',    label: 'Import / Export' },
  { key: 'downloads', label: 'Project Downloads' },
  { key: 'pto',       label: 'PTO Tracker' },
  { key: 'scenescrub', label: 'Scene Scrub' },
  { key: 'scrubqueue', label: 'Scrub Queue' },   // Q-2 (D-179): Admin role only — the API refuses everyone else
];

export default function Admin() {
  const { hasRole } = useAuth();
  const isAdmin         = hasRole('Admin');
  const isPowerUserOnly = hasRole('Power User') && !hasRole('Admin');
  // Admins manage all members; Power Users / Producers get a talent-scoped
  // Team Members tab (manage talent only). Other staff don't see the tab.
  const canManageTalent = isAdmin || hasRole('Power User') || hasRole('Producer');
  // Client Downloads (bulk client handoff export) is Admin / Power User only.
  const canDownloadClients = isAdmin || hasRole('Power User');
  // PTO Tracker (HR module) is Admin / Manager only — NOT Power User.
  const canManagePto = isAdmin || hasRole('Manager');
  const visibleTabs = TABS.filter(t => {
    if (t.key === 'clients'   && isPowerUserOnly) return false;
    if (t.key === 'team'      && !canManageTalent) return false;
    if (t.key === 'downloads' && !canDownloadClients) return false;
    if (t.key === 'pto'       && !canManagePto) return false;
    if (t.key === 'scenescrub' && !canDownloadClients) return false;   // Admin / Power User
    if (t.key === 'scrubqueue' && !isAdmin) return false;              // Q-2: the meter, the queue control and the budget are the Admin's
    return true;
  });
  const defaultTab = isAdmin
    ? 'clients'
    : (canManageTalent ? 'team' : (canManagePto ? 'pto' : (isPowerUserOnly ? 'storage' : 'clients')));
  const [tab, setTab] = useState(defaultTab);

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Admin</div>
          <div className="page-sub">Manage projects, team members, talent, storage, and audit log</div>
        </div>
        <div className="topbar-actions">
          <div className="adm-tabs">
            {visibleTabs.map(t => (
              <button
                key={t.key}
                className={`adm-tab ${tab === t.key ? 'active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="body">
        {tab === 'clients' && !isPowerUserOnly && <ClientsTab />}
        {tab === 'team'    && canManageTalent && <TeamTab talentScoped={!isAdmin} />}
        {tab === 'storage' && <StorageTab />}
        {tab === 'audit'   && <AuditTab />}
        {tab === 'data'    && <DataTab />}
        {tab === 'downloads' && canDownloadClients && <ClientDownloads />}
        {tab === 'pto'       && canManagePto && <PtoTracker />}
        {tab === 'scenescrub' && canDownloadClients && <SceneScrubAdmin />}
        {tab === 'scrubqueue' && isAdmin && <ScrubQueueAdmin />}
      </div>
    </div>
  );
}
